import React from 'react';
import { NotificationItem, NotificationPriority } from '../types/notifications.types';
import {
  Bell,
  Info,
  AlertTriangle,
  AlertCircle,
  FileText,
  Calendar,
  CheckCircle2,
  Users,
} from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui';

interface NotificationCardProps {
  item: NotificationItem;
  isSelected?: boolean;
  onSelect?: (checked: boolean) => void;
  onClick?: (item: NotificationItem) => void;
}

export const NotificationCard: React.FC<NotificationCardProps> = ({
  item,
  isSelected = false,
  onSelect,
  onClick,
}) => {
  const isUnread = item.status_code === 'unread';

  const getPriorityConfig = (priority: NotificationPriority, type: string) => {
    // Map icons based on type or priority
    if (type.includes('approval') || type.includes('request')) {
      return { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' };
    }
    if (type.includes('deadline') || type.includes('schedule')) {
      return { icon: Calendar, color: 'text-amber-600', bg: 'bg-amber-50' };
    }
    if (type.includes('team') || type.includes('user')) {
      return { icon: Users, color: 'text-indigo-600', bg: 'bg-indigo-50' };
    }
    if (priority === 'urgent') {
      return { icon: AlertTriangle, color: 'text-rose-600', bg: 'bg-rose-50' };
    }
    if (priority === 'high') {
      return { icon: AlertCircle, color: 'text-orange-600', bg: 'bg-orange-50' };
    }
    if (type.includes('approved') || type.includes('success')) {
      return { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' };
    }
    return { icon: Bell, color: 'text-purple-600', bg: 'bg-purple-50' };
  };

  const config = getPriorityConfig(item.priority_code, item.notification_type);
  const Icon = config.icon;

  return (
    <div
      className={`group flex items-center gap-4 p-3 transition-colors cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50 ${
        isUnread ? 'bg-slate-50/50' : 'bg-white'
      }`}
      onClick={() => onClick?.(item)}
    >
      {/* Checkbox */}
      <div className="shrink-0 pl-2" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect?.(e.target.checked)}
          className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-600 cursor-pointer"
        />
      </div>

      {/* Icon */}
      <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${config.bg}`}>
        <Icon className={`w-5 h-5 ${config.color}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
        <div className="md:col-span-8 space-y-1">
          <div className="flex items-center gap-2">
            <h4 className={`sm:text-sm text-xs truncate ${isUnread ? 'text-slate-900' : 'text-slate-600'}`}>
              {item.title}
            </h4>
            {isUnread && (
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"></span>
            )}
          </div>
          <p className="text-xs text-slate-500 truncate">
            {item.project?.name && `Dự án: ${item.project.name} - `} {item.message}
          </p>
        </div>

        {/* Time and Date */}
        <div className="md:col-span-2 hidden md:block text-right">
          <div className="sm:text-sm text-xs text-slate-700">{format(new Date(item.created_at), 'HH:mm')}</div>
          <div className="text-[10px] text-slate-500">{format(new Date(item.created_at), 'dd/MM/yyyy')}</div>
        </div>

        {/* Status Badge */}
        <div className="md:col-span-2 flex justify-end">
          {isUnread ? (
            <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border-0 sm:text-sm text-xs rounded-md">
              Chưa đọc
            </Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 border-0 sm:text-sm text-xs rounded-md">
              Đã đọc
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};
